import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { admin, isApiError } from '@repletoryreach/sdk-api';
import { formatMinor } from '../lib/format';
import { useT } from '../i18n/I18nProvider';

export type AuditSource = 'transfers' | 'opt-in' | 'deliveries' | 'flow-runs' | 'ledger' | 'impersonations';

export const AUDIT_SOURCES: AuditSource[] = [
  'transfers', 'opt-in', 'deliveries', 'flow-runs', 'ledger', 'impersonations'
];

export interface AuditRow {
  source: AuditSource;
  id: string;
  occurred_at: string;
  organisation_id: string;
  /** The one line an operator scans: what happened. */
  summary: string;
  /** The qualifier under it: amount, attempt, status, reason. */
  detail?: string;
  /** The record as the endpoint returned it, for the detail panel. */
  raw: Record<string, unknown>;
}

export interface AuditFeed {
  rows: AuditRow[];
  loading: boolean;
  fetching: boolean;
  /** Sources whose query failed. An audit log that hides these is lying. */
  failedSources: AuditSource[];
  /** True when at least one source answered 403, which is not the same as "no events". */
  denied: boolean;
  /** True when any source filled its own quota, so the timeline is a truncated view. */
  capped: boolean;
  /**
   * The true count in the window for the sources that report one. Absent for a
   * source whose endpoint still returns rows without a window total, because
   * inferring a total from a capped row count is the lie the meta block exists
   * to end.
   */
  sourceTotals: Partial<Record<AuditSource, number>>;
  updatedAt: number;
  refetch: () => void;
}

/**
 * Fans in the six admin-audit endpoints into a single sorted timeline.
 *
 * The sixth is impersonations, added because it was the one feed in the
 * product that knows WHO did something and the only one missing from the page
 * called "Audit log". The other five record what happened and, with one
 * exception, not who: an operator minting a token into a customer's account is
 * the highest-risk action here and the only one already attributed.
 * Each source is a separate react-query so a slow one doesn't block the rest.
 *
 * Two properties this hook has to guarantee, because the page cannot recover
 * them afterwards. First, a failed source is reported rather than merged in as
 * an absence: "we could not ask" and "nothing happened" look identical once
 * the rows are concatenated, and only the first of those is a reason to keep
 * investigating. Second, the row cap is applied per source before the merge,
 * so a chatty source cannot crowd the other four out of the window.
 */
export function useAuditFeed(
  since: string,
  enabled: Record<AuditSource, boolean>,
  limit = 200
): AuditFeed {
  // Each source gets the same allowance, so the busiest one cannot starve the
  // rest. The old code sliced after the merge, which let 500 ledger rows hide
  // every transfer in the same window.
  const perSource = Math.max(1, Math.ceil(limit / AUDIT_SOURCES.length));

  // The five feeds are summarised into one sentence each, and those sentences
  // are the column an operator actually reads. They were assembled here in
  // English template literals, which put the audit trail outside the catalogue
  // the rest of the console is translated through.
  const t = useT();

  const transfers = useQuery({
    queryKey: ['audit', 'transfers', since],
    queryFn: () => admin.listTransfers(since),
    enabled: enabled.transfers
  });
  const optIn = useQuery({
    queryKey: ['audit', 'opt-in', since],
    queryFn: () => admin.listOptInEvents(since),
    enabled: enabled['opt-in']
  });
  // Deliveries and the ledger bound the window on the server and report what
  // the window really holds, so both ask for their own share of the budget
  // rather than the newest 500 rows of everything.
  const deliveries = useQuery({
    queryKey: ['audit', 'deliveries', since, perSource],
    queryFn: () => admin.listDeliveries({ since, limit: perSource }),
    enabled: enabled.deliveries
  });
  const flowRuns = useQuery({
    queryKey: ['audit', 'flow-runs', since],
    queryFn: () => admin.listFlowRuns(since),
    enabled: enabled['flow-runs']
  });
  const ledger = useQuery({
    queryKey: ['audit', 'ledger', since, perSource],
    queryFn: () => admin.listLedger({ since, limit: perSource }),
    enabled: enabled.ledger
  });
  // The endpoint takes no window and returns the newest 100 mints, so the
  // window is applied here. Filtering client-side is right for exactly this
  // feed: 100 rows is the whole thing, not a page of it.
  const impersonations = useQuery({
    queryKey: ['audit', 'impersonations'],
    queryFn: () => admin.listImpersonations(),
    enabled: enabled.impersonations
  });

  const rows = useMemo(() => {
    const transferRows: AuditRow[] = (transfers.data?.data ?? []).slice(0, perSource).map((r) => ({
      source: 'transfers' as const,
      id: r.id,
      occurred_at: r.occurred_at,
      organisation_id: r.organisation_id,
      summary: t('audit.summary.transferred'),
      detail: r.reason ? t('audit.detail.transferReason', { reason: r.reason }) : undefined,
      raw: r as unknown as Record<string, unknown>
    }));

    const optInRows: AuditRow[] = (optIn.data?.data ?? []).slice(0, perSource).map((r) => ({
      source: 'opt-in' as const,
      id: r.id,
      occurred_at: r.occurred_at,
      organisation_id: r.organisation_id,
      summary: r.opted_in
        ? t('audit.summary.optIn', { channel: r.channel })
        : t('audit.summary.optOut', { channel: r.channel }),
      detail: r.source ? t('audit.detail.optInSource', { source: r.source }) : undefined,
      raw: r as unknown as Record<string, unknown>
    }));

    const deliveryRows: AuditRow[] = (deliveries.data?.data ?? []).slice(0, perSource).map((r) => ({
      source: 'deliveries' as const,
      id: r.id,
      occurred_at: r.scheduled_at,
      organisation_id: r.organisation_id,
      summary: t('audit.summary.deliveryEvent', { event: r.event_type }),
      // A delivery the SSRF guard refused never leaves the service, so it has
      // no HTTP status. Reporting it as a blank status hides a W45 refusal.
      detail: r.blocked_reason
        ? t('audit.detail.deliveryBlocked', { attempt: r.attempt_number, reason: r.blocked_reason })
        : r.http_status == null
          ? t('audit.detail.deliveryNoStatus', { attempt: r.attempt_number, result: r.result })
          : t('audit.detail.delivery', {
              attempt: r.attempt_number,
              status: r.http_status,
              ms: r.latency_ms ?? 0
            }),
      raw: r as unknown as Record<string, unknown>
    }));

    const flowRunRows: AuditRow[] = (flowRuns.data?.data ?? []).slice(0, perSource).map((r) => ({
      source: 'flow-runs' as const,
      id: r.id,
      occurred_at: r.started_at,
      organisation_id: r.organisation_id,
      // The flow's name is what a person calls the run. The id behind it is a
      // join key and stays in the detail panel.
      summary: r.flow_name ?? t('audit.summary.flowRun'),
      detail: r.contact_msisdn
        ? t('audit.detail.flowRunFor', { status: r.status, channel: r.channel, msisdn: r.contact_msisdn })
        : t('audit.detail.flowRun', { status: r.status, channel: r.channel }),
      raw: r as unknown as Record<string, unknown>
    }));

    const ledgerRows: AuditRow[] = (ledger.data?.data ?? []).slice(0, perSource).map((r) => {
      const sku = r.sku_code ?? t('audit.summary.ledgerAdjustment');
      const amount = formatMinor(r.amount_minor, r.currency);
      return {
        source: 'ledger' as const,
        id: r.id,
        occurred_at: r.occurred_at,
        organisation_id: r.organisation_id,
        // `entry_type` is the readable code the projection now carries beside
        // the lookup id, so the row can say "topup" instead of a GUID.
        summary: r.description ?? t('audit.summary.ledgerEntry', { type: r.entry_type }),
        detail: t('audit.detail.ledger', { sku, amount, direction: r.direction }),
        raw: r as unknown as Record<string, unknown>
      };
    });

    const impersonationRows: AuditRow[] = (impersonations.data?.data ?? [])
      .filter((r) => r.created_at >= since)
      .slice(0, perSource)
      .map((r) => ({
        source: 'impersonations' as const,
        id: r.id,
        occurred_at: r.created_at,
        organisation_id: r.tenant_id ?? '',
        // The operator's address leads, because on this one feed the "who" is
        // the whole point and every other row on the page cannot answer it.
        summary: t('audit.summary.impersonation', { admin: r.admin_email ?? t('audit.summary.impersonationUnknown') }),
        detail: r.expires_at
          ? t('audit.detail.impersonation', { tenant: r.tenant_name ?? '', expires: r.expires_at })
          : t('audit.detail.impersonationNoExpiry', { tenant: r.tenant_name ?? '' }),
        raw: r as unknown as Record<string, unknown>
      }));

    return [...transferRows, ...optInRows, ...deliveryRows, ...flowRunRows, ...ledgerRows, ...impersonationRows]
      .sort((a, b) => (a.occurred_at < b.occurred_at ? 1 : -1));
  }, [transfers.data, optIn.data, deliveries.data, flowRuns.data, ledger.data, impersonations.data, perSource, since, t]);

  const queries = useMemo(
    () => [
      { source: 'transfers' as const, q: transfers },
      { source: 'opt-in' as const, q: optIn },
      { source: 'deliveries' as const, q: deliveries },
      { source: 'flow-runs' as const, q: flowRuns },
      { source: 'ledger' as const, q: ledger },
      { source: 'impersonations' as const, q: impersonations }
    ],
    [transfers, optIn, deliveries, flowRuns, ledger, impersonations]
  );

  const failedSources = queries.filter(({ q }) => q.error).map(({ source }) => source);
  const denied = queries.some(({ q }) => isApiError(q.error) && q.error.status === 403);
  const loading = queries.some(({ q }) => q.isLoading);
  const fetching = queries.some(({ q }) => q.isFetching);
  const updatedAt = Math.max(0, ...queries.map(({ q }) => q.dataUpdatedAt));

  const sourceTotals: Partial<Record<AuditSource, number>> = {};
  if (deliveries.data?.meta) sourceTotals.deliveries = deliveries.data.meta.total;
  if (ledger.data?.meta) sourceTotals.ledger = ledger.data.meta.total;

  // Where the endpoint says whether it truncated, believe it. The three feeds
  // that still answer without a window meta can only be guessed at, and a full
  // page is the only signal they give.
  const capped =
    Boolean(deliveries.data?.meta?.capped) ||
    Boolean(ledger.data?.meta?.capped) ||
    (transfers.data?.data.length ?? 0) >= perSource ||
    (optIn.data?.data.length ?? 0) >= perSource ||
    (flowRuns.data?.data.length ?? 0) >= perSource ||
    // The impersonation endpoint caps itself at 100 with no meta, so a full
    // hundred is the only signal that there were more.
    (impersonations.data?.data.length ?? 0) >= 100;

  const refetch = useCallback(() => {
    for (const { q } of queries) void q.refetch();
  }, [queries]);

  return { rows, loading, fetching, failedSources, denied, capped, sourceTotals, updatedAt, refetch };
}
